'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ComponentType, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { type CommentsPage, useComments } from '@/lib/api/comments';
import { Button } from '@/lib/ui-kit/button';
import { Empty } from '../Empty';
import { buildThreadedComments, type ThreadedComment } from './buildThreadedComments';
import { useCommentsContext } from './CommentsContext';
import { scrollToComment } from './scrollToComment';

interface CommentsListProps {
  Item: ComponentType<{ comment: ThreadedComment; depth?: number }>;
  pageSize?: number;
  initialPage?: CommentsPage;
  emptyFallback?: ReactNode;
  errorFallback?: ReactNode;
}

export const CommentsList = ({
  Item,
  pageSize,
  initialPage,
  emptyFallback,
  errorFallback,
}: CommentsListProps) => {
  const t = useTranslations('comments');
  const tError = useTranslations('errors.global');
  const { source, sort, onlyHolders, onlyMine } = useCommentsContext();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch } =
    useComments(source, { sort, onlyHolders, onlyMine, pageSize, initialPage });

  const sentinelRef = useRef<HTMLDivElement>(null);

  const threaded = useMemo(() => {
    const flat = data?.pages.flatMap((page) => page.comments) ?? [];
    return buildThreadedComments(flat);
  }, [data]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { rootMargin: '400px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Center a comment deep-linked from a notification (#comment-<id>). The 400ms
  // re-run corrects for late-loading content above the thread shifting it.
  useEffect(() => {
    if (threaded.length === 0) return;
    const go = () => {
      if (location.hash.startsWith('#comment-')) scrollToComment(location.hash.slice(9));
    };
    go();
    const t = setTimeout(go, 400);
    window.addEventListener('hashchange', go);
    return () => {
      clearTimeout(t);
      window.removeEventListener('hashchange', go);
    };
  }, [threaded]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-txt-3 h-6 w-6 animate-spin" />
      </div>
    );
  }

  // A failed load must never look like an empty thread: without its own copy the default
  // <Empty /> reads as "no comments yet", which hides 4xx/5xx from the user entirely.
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        {errorFallback ?? <Empty title={tError('title')} desc={t('failedToLoad')} />}
        <Button
          variant="outlined"
          size="sm"
          className="h-[32px] w-fit rounded-sm !px-4 !py-2"
          onClick={() => refetch()}
        >
          {tError('tryAgain')}
        </Button>
      </div>
    );
  }

  if (threaded.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">{emptyFallback ?? <Empty />}</div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {threaded.map((comment) => (
        <Item key={comment.id} comment={comment} />
      ))}
      {hasNextPage && <div ref={sentinelRef} />}
    </div>
  );
};
